import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import "./home.css";

// Modal component
const Modal = ({
  isOpen,
  onClose,
  onSubmit,
  appData,
  appGroups,
  onAddGroup,
  loading,
}) => {
  const [appName, setAppName] = useState(appData ? appData.name : "");
  const [appUrl, setAppUrl] = useState(appData ? appData.url : "");
  const [appAccess, setAppAccess] = useState(
    Array.isArray(appData?.access) ? [...appData.access] : []
  );

  useEffect(() => {
    if (isOpen && appData) {
      setAppName(appData.name);
      setAppUrl(appData.url);

      // Convert access emails to { email, name } using appGroups
      const formattedAccess = Array.isArray(appData.access)
        ? appData.access.map((email) => {
            const matchedGroup = appGroups.find((g) => g.email === email);
            return {
              email,
              name: matchedGroup?.name || email, // fallback naar email als naam ontbreekt
            };
          })
        : [];

      setAppAccess(formattedAccess);
    }
  }, [isOpen, appData, appGroups]);

  const handleSubmit = () => {
    onSubmit(appName, appUrl, appAccess);
  };

  const handleAddGroupToAccess = (group) => {
    setAppAccess((prevAccess) => {
      const exists = prevAccess.some((entry) => entry.email === group.email);
      if (!exists) {
        return [...prevAccess, { email: group.email, name: group.name }];
      }
      return prevAccess;
    });
    onAddGroup(group);
  };

  const handleRemoveAccess = (emailToRemove) => {
    setAppAccess((prevAccess) =>
      prevAccess.filter((entry) => entry.email !== emailToRemove)
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content-styled">
        <h3>Edit Application</h3>

        <div className="modal-form-group">
          <label className="modal-label">Application Name:</label>
          <input
            type="text"
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            placeholder="Enter Application Name"
            className="modal-input"
          />
        </div>

        <div className="modal-form-group">
          <label className="modal-label">Application URL:</label>
          <input
            type="text"
            value={appUrl}
            onChange={(e) => setAppUrl(e.target.value)}
            placeholder="Enter Application URL"
            className="modal-input"
          />
        </div>

        <div className="modal-form-group">
          <label className="modal-label">Access:</label>
          <div className="pills-container">
            {appAccess.map((accessItem) => (
              <button
                key={accessItem.email}
                className="pill delete-group-pill"
                onClick={() => handleRemoveAccess(accessItem.email)}
                title="Click to remove"
              >
                {accessItem.name}
              </button>
            ))}

            {appAccess.length === 0 && (
              <span className="empty-message">No access added yet.</span>
            )}
          </div>
        </div>

        {/* Add Groups Section with Loader */}
        <div className="modal-form-group">
          <label className="modal-label">Add Groups:</label>
          <div className="pills-container">
            {loading ? (
              <div className="loader">
                <span></span>
                <span></span>
                <span></span>
              </div> // Toon de drie stippen loader
            ) : (
              appGroups.map((group) => (
                <button
                  key={group.email}
                  className="pill add-group-pill"
                  onClick={() => handleAddGroupToAccess(group)}
                >
                  {group.name}
                </button>
              ))
            )}
            {appGroups.length === 0 && !loading && (
              <span className="empty-message">No app groups found.</span>
            )}
          </div>
        </div>

        <div className="modal-actions-styled">
          <button onClick={handleSubmit} className="modal-button primary">
            Update Application
          </button>
          <button onClick={onClose} className="modal-button secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Apps component
const Apps = () => {
  const [user, setUser] = useState(null);
  const [applications, setApplications] = useState([]);
  const [newAppName, setNewAppName] = useState("");
  const [newAppUrl, setNewAppUrl] = useState("");
  const [newAppAccess, setNewAppAccess] = useState([]);
  const [editingAppId, setEditingAppId] = useState(null);
  const [editingAppName, setEditingAppName] = useState("");
  const [editingAppUrl, setEditingAppUrl] = useState("");
  const [editingAppAccess, setEditingAppAccess] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [appGroups, setAppGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Authenticate the user
  const checkUserAuth = () => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
    });
  };

  // Fetch applications from Firestore
  const fetchApplications = async () => {
    const applicationsRef = collection(db, "applications");
    const snapshot = await getDocs(applicationsRef);
    const appsList = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setApplications(appsList);
  };

  // Fetch applications and authenticate on page load
  useEffect(() => {
    checkUserAuth();
    if (user) {
      fetchApplications();
    }
  }, [user]);

  // Function to fetch app groups from the Google Cloud function
  const fetchAppGroups = async () => {
    setLoading(true); // Zet loading op true wanneer we beginnen met het ophalen van groepen
    try {
      const response = await fetch(
        "http://127.0.0.1:5001/webportaal/us-central1/getAppGroups", // Update met je API URL
        {
          method: "GET",
        }
      );
      const data = await response.json();
      setAppGroups(data.groups || []); // Veronderstel dat de API een object met een "groups" veld retourneert
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setLoading(false); // Zet loading op false wanneer de data is opgehaald of als er een fout is
    }
  };

  // Handle selection change in the dropdown
  const handleAppSelect = (event) => {
    const appId = event.target.value;
    const app = applications.find((app) => app.id === appId);
    setEditingAppId(app.id);
    setEditingAppName(app.name);
    setEditingAppUrl(app.url);
    setEditingAppAccess(app.access);
  };

  // Handle app editing
  const handleEditClick = () => {
    if (editingAppId) {
      setIsEditModalOpen(true); // Open the modal to edit the selected app
      fetchAppGroups(); // Fetch app groups when editing
    }
  };

  // Add a new application
  const addApplication = async () => {
    if (newAppName && newAppUrl) {
      try {
        const applicationsRef = collection(db, "applications");
        await addDoc(applicationsRef, {
          name: newAppName,
          url: newAppUrl?.toLowerCase() || "", // Use optional chaining and fallback
          // access: newAppAccess,
        });
        fetchApplications();
        setNewAppName("");
        setNewAppUrl("");
        setNewAppAccess([]);
      } catch (error) {
        console.error("Error adding application: ", error);
      }
    } else {
      alert("Please provide a name, URL, and access list for the application.");
    }
  };

  // Update an existing application
  const updateApplication = async (appName, appUrl, appAccess) => {
    // if (appAccess && appAccess.length > 0) {
    try {
      const appRef = doc(db, "applications", editingAppId);
      await updateDoc(appRef, {
        name: appName,
        url: appUrl ? appUrl.toLowerCase() : "",
        access: appAccess.map((item) => item.email), // alleen e-mails opslaan
      });
      fetchApplications();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Error updating application: ", error);
    }
    // } else {
    //   alert("Please provide access details.");
    // }
  };

  // Add group to the app access list
  const handleAddGroup = (group) => {
    setEditingAppAccess((prevAccess) => {
      // Ensure we're working with an array
      const newAccess = Array.isArray(prevAccess) ? [...prevAccess] : [];
      if (!newAccess.includes(group.email)) {
        newAccess.push(group.email); // Add the new group to the array
      }
      return newAccess;
    });
  };

  // Delete application
  const deleteApplication = async () => {
    try {
      const appRef = doc(db, "applications", editingAppId);
      await deleteDoc(appRef);
      fetchApplications();
    } catch (error) {
      console.error("Error deleting application: ", error);
    }
  };

  return (
    <div className="apps-container">
      {user && (
        <section className="add-app-section">
          <div className="h2div">Add Application</div>
          <div className="form-grid">
            <div className="form-group-styled">
              <label htmlFor="newAppName" className="form-label-styled">
                Application Name:
              </label>
              <input
                type="text"
                id="newAppName"
                placeholder="Application Name"
                value={newAppName}
                onChange={(e) => setNewAppName(e.target.value)}
                className="app-name"
              />
            </div>
            <div className="form-group-styled">
              <label htmlFor="newAppUrl" className="form-label-styled">
                Application URL:
              </label>
              <input
                type="text"
                id="newAppUrl"
                placeholder="https://"
                value={newAppUrl} // Add the prefix to the value
                onChange={(e) => setNewAppUrl(e.target.value)}
                className="app-url"
              />
            </div>
            {/* <div className="form-group-styled">
              <label htmlFor="newAppAccess" className="form-label-styled">
                Access (comma separated):
              </label>
              <textarea
                id="newAppAccess"
                value={newAppAccess.join(",")}
                onChange={(e) =>
                  setNewAppAccess(
                    e.target.value.split(",").map((item) => item.trim())
                  )
                }
                placeholder="Enter Access Details"
                className="app-access"
              />
            </div> */}
          </div>
          <button onClick={addApplication} className="button-styled primary">
            Add Application
          </button>
        </section>
      )}

      <section className="applications-list-section">
        <div className="h2div">Edit Application</div>
        <div className="dropdown-container">
          <select
            className="app-select"
            onChange={handleAppSelect}
            value={editingAppId || ""}
          >
            <option value="" disabled>
              Select an Application
            </option>
            {applications
              .sort((a, b) => a.name.localeCompare(b.name)) // Sort applications alphabetically by name
              .map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
          </select>

          <button
            className="button-styled edit"
            onClick={handleEditClick}
            disabled={!editingAppId}
          >
            Edit
          </button>
          <button
            className="button-styled delete"
            onClick={deleteApplication}
            disabled={!editingAppId}
          >
            Delete
          </button>
        </div>
      </section>

      {/* Edit Application Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={updateApplication}
        appData={{
          name: editingAppName,
          url: editingAppUrl,
          access: editingAppAccess,
        }}
        appGroups={appGroups}
        onAddGroup={handleAddGroup}
        loading={loading}
      />
    </div>
  );
};

export default Apps;
